import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate, useParams } from 'react-router-dom';
import * as Icons from '../constants/icons';
import LoadingView from '../components/LoadingView';
import MailBlocksEditor from '../components/mailDelivery/MailBlocksEditor';
import MailPreviewFrame from '../components/mailDelivery/MailPreviewFrame';
import MailListMembersDialog from '../components/mailDelivery/MailListMembersDialog';
import { CampaignStatusChip } from '../components/mailDelivery/MailStatusChip';
import { PAGE_FILL_SX } from '../constants/layout';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useAuth } from '../firebase/AuthProvider';
import {
  useMailMasters,
  useMailCampaign,
  mailApi,
  MailBlock,
  MailCampaignInput,
  MailTemplate,
  MailList,
  RecipientCount,
  fmtDateTime,
  isDriveLink,
} from '../hooks/useMailDelivery';

/**
 * メール配信 配信作成・編集 (/mail-delivery/campaigns/new, /mail-delivery/campaigns/:id/edit)。
 *
 * ブラストメールと同じ 5 ステップ (配信先 → 内容 → テスト配信 → 配信日時 → 確認)。
 * 途中保存は「下書き保存」(いつでも可)。本文はブロック組み立て方式で、右側にサーバー生成の
 * プレビュー (CSS インライン化・差し込み見本つき) を表示する。送信済み (sending 以降) の配信は
 * 編集できないため詳細ページへ送る。
 */

const STEPS = ['配信先', '内容', 'テスト配信', '配信日時', '確認'];
const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' }, '& .MuiInputLabel-root': { fontSize: '13px' } };

const emptyForm = (): MailCampaignInput => ({
  subject: '',
  from_address_id: null,
  reply_to: null,
  template_id: null,
  blocks_json: [],
  body_text: null,
  list_codes: [],
  filter_json: null,
  scheduled_at: null,
});

/** datetime-local の値 ("YYYY-MM-DDTHH:mm") を JST オフセット付き ISO にする */
const toJstIso = (local: string): string | null => (local ? `${local}:00+09:00` : null);
/** ISO (オフセット付き) を datetime-local の値にする */
const toLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** ブロックの入力不備 (サーバーの validate_blocks と同じ条件) */
function blockErrors(blocks: MailBlock[]): string[] {
  const errs: string[] = [];
  blocks.forEach((b, i) => {
    if (b.type === 'image' && !b.url) errs.push(`${i + 1} 番目の画像ブロックに URL がありません`);
    if (b.type === 'image' && isDriveLink(b.url)) errs.push(`${i + 1} 番目の画像に Google Drive のリンクが入っています (「画像をアップロード」から登録してください)`);
    if (b.type === 'button' && !b.url) errs.push(`${i + 1} 番目のボタンブロックにリンク先がありません`);
  });
  return errs;
}

const MailDeliveryCampaignEditor: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const editingId = idParam ? Number(idParam) : null;
  const navigate = useNavigate();
  const notify = useNotification();
  const confirm = useConfirm();
  const { user } = useAuth();
  const operator = user?.email || 'unknown';

  const masters = useMailMasters();
  const existing = useMailCampaign(editingId);

  const [form, setForm] = useState<MailCampaignInput>(emptyForm);
  const [step, setStep] = useState(0);
  const [initialized, setInitialized] = useState(editingId == null);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);

  // 配信先の件数
  const [recipients, setRecipients] = useState<RecipientCount | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  // プレビュー
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const previewTimer = useRef<number | null>(null);
  // テスト配信
  const [testTo, setTestTo] = useState(user?.email || '');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  // 配信日時
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduleLocal, setScheduleLocal] = useState('');
  // テンプレート保存ダイアログ
  const [tplDialog, setTplDialog] = useState(false);
  const [tplName, setTplName] = useState('');
  // 配信リストの中身ダイアログ (変更したら件数を数え直す)
  const [membersList, setMembersList] = useState<MailList | null>(null);
  const [countKey, setCountKey] = useState(0);

  // 既存配信の読み込み (下書き・予約のみ編集可)
  useEffect(() => {
    if (editingId == null || existing.loading || initialized) return;
    const c = existing.campaign;
    if (!c) {
      setInitialized(true);
      return;
    }
    if (c.status !== 'draft' && c.status !== 'scheduled') {
      notify.warning('送信済みの配信は編集できません。複製して新しい配信を作成してください');
      navigate(`/mail-delivery/campaigns/${c.id}`, { replace: true });
      return;
    }
    setForm({
      id: c.id,
      subject: c.subject || '',
      from_address_id: c.from_address_id,
      reply_to: c.reply_to,
      template_id: c.template_id,
      copied_from_id: c.copied_from_id,
      blocks_json: Array.isArray(c.blocks_json) ? c.blocks_json : [],
      body_text: c.body_text,
      list_codes: c.list_codes || [],
      filter_json: c.filter_json,
      scheduled_at: c.scheduled_at,
    });
    if (c.scheduled_at) {
      setSendMode('schedule');
      setScheduleLocal(toLocalInput(c.scheduled_at));
    }
    setInitialized(true);
  }, [editingId, existing.loading, existing.campaign, initialized, navigate, notify]);

  // 新規作成時は既定の送信元と既定の配信リスト (建売・土地情報) を入れる
  useEffect(() => {
    if (editingId != null || masters.loading) return;
    setForm((prev) => {
      if (prev.from_address_id) return prev;
      const def = masters.fromAddresses.find((f) => f.is_default) || masters.fromAddresses[0];
      const defList = masters.lists.find((l) => l.list_code === 'sale_land');
      return {
        ...prev,
        from_address_id: def ? def.id : null,
        list_codes: prev.list_codes.length ? prev.list_codes : defList ? [defList.list_code] : [],
      };
    });
  }, [editingId, masters.loading, masters.fromAddresses, masters.lists]);

  // 配信先の件数集計 (リスト・絞り込みが変わるたび)
  useEffect(() => {
    if (!initialized) return;
    if (form.list_codes.length === 0) {
      setRecipients(null);
      return;
    }
    let alive = true;
    setCountLoading(true);
    mailApi.countRecipients(form.list_codes, form.filter_json).then((r) => {
      if (!alive) return;
      setRecipients(r.ok ? r.data : null);
      setCountLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [initialized, form.list_codes, form.filter_json, countKey]);

  // プレビュー (ブロック変更から 700ms 後に生成)
  const refreshPreview = useCallback(() => {
    if (form.blocks_json.length === 0) {
      setPreviewHtml(null);
      setPreviewText(null);
      return;
    }
    mailApi.preview(form.blocks_json, form.body_text).then((r) => {
      if (r.ok) {
        setPreviewHtml(r.data.html);
        setPreviewText(r.data.text);
      }
    });
  }, [form.blocks_json, form.body_text]);

  useEffect(() => {
    if (!initialized || (step !== 1 && step !== 4)) return;
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(refreshPreview, 700);
    return () => {
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
    };
  }, [initialized, step, refreshPreview]);

  const set = <K extends keyof MailCampaignInput>(key: K, value: MailCampaignInput[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const fromAddress = masters.fromAddresses.find((f) => f.id === form.from_address_id) || null;
  const selectedLists = masters.lists.filter((l) => form.list_codes.includes(l.list_code));
  const scheduledIso = sendMode === 'schedule' ? toJstIso(scheduleLocal) : null;
  const scheduleInPast = sendMode === 'schedule' && !!scheduledIso && new Date(scheduledIso).getTime() <= Date.now();

  // ステップごとの進行条件
  const stepError = useMemo((): string | null => {
    if (step === 0 && form.list_codes.length === 0) return '配信リストを 1 つ以上選択してください';
    if (step === 1) {
      if (!form.from_address_id) return '送信元を選択してください';
      if (!form.subject.trim()) return '件名を入力してください';
      if (form.blocks_json.length === 0) return '本文のブロックを追加してください';
      const errs = blockErrors(form.blocks_json);
      if (errs.length) return errs[0];
    }
    if (step === 3) {
      if (sendMode === 'schedule' && !scheduleLocal) return '配信日時を入力してください';
      if (scheduleInPast) return '配信日時は現在より後にしてください';
    }
    return null;
  }, [step, form, sendMode, scheduleLocal, scheduleInPast]);

  // 下書き保存 (新規なら URL を編集 URL に差し替える)
  const saveDraft = useCallback(
    async (silent = false): Promise<number | null> => {
      if (!form.subject.trim() || !form.from_address_id) {
        if (!silent) notify.warning('件名と送信元を入力してから保存してください');
        return null;
      }
      setSaving(true);
      const r = await mailApi.saveCampaign({ ...form, scheduled_at: scheduledIso }, operator);
      setSaving(false);
      if (!r.ok) {
        notify.error(r.error);
        return null;
      }
      const id = r.data.id;
      if (!form.id) {
        setForm((prev) => ({ ...prev, id }));
        navigate(`/mail-delivery/campaigns/${id}/edit`, { replace: true });
      }
      if (!silent) notify.success('下書きを保存しました');
      return id;
    },
    [form, scheduledIso, operator, navigate, notify],
  );

  const handleSendTest = async () => {
    const to = testTo.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
    if (to.length === 0) return notify.warning('テスト送信先を入力してください');
    if (!form.from_address_id || form.blocks_json.length === 0) return notify.warning('内容 (送信元・本文) を先に入力してください');
    setTestSending(true);
    setTestResult(null);
    const r = await mailApi.sendTest(form, to);
    setTestSending(false);
    if (!r.ok) return notify.error(r.error);
    setTestResult(`${r.data.sent} 件に送信しました (${fmtDateTime(new Date().toISOString())})。件名の先頭に【テスト】が付きます。`);
    notify.success('テスト送信しました');
  };

  const handleStart = async () => {
    if (stepError) return notify.warning(stepError);
    const count = recipients?.count ?? 0;
    const ok = await confirm({
      title: sendMode === 'schedule' ? '配信を予約' : '配信を開始',
      message:
        sendMode === 'schedule'
          ? `${fmtDateTime(scheduledIso)} に ${count.toLocaleString()} 件へ配信を予約します。よろしいですか？`
          : `${count.toLocaleString()} 件へ今すぐ配信を開始します。開始後は取り消せません。よろしいですか？`,
      confirmText: sendMode === 'schedule' ? '予約する' : '配信する',
    });
    if (!ok) return;
    setStarting(true);
    const id = await saveDraft(true);
    if (!id) {
      setStarting(false);
      return;
    }
    const r = await mailApi.startCampaign(id, operator, scheduledIso);
    setStarting(false);
    if (!r.ok) return notify.error(r.error);
    notify.success(r.data.campaign_status === 'scheduled' ? `配信を予約しました (${fmtDateTime(r.data.scheduled_at)})` : `配信を開始しました (${r.data.total.toLocaleString()} 件)`);
    navigate(`/mail-delivery/campaigns/${id}`, { replace: true });
  };

  const applyTemplate = (t: MailTemplate) => {
    setForm((prev) => ({
      ...prev,
      template_id: t.id,
      subject: prev.subject || t.subject || '',
      from_address_id: t.from_address_id || prev.from_address_id,
      blocks_json: JSON.parse(JSON.stringify(t.blocks_json || [])),
      body_text: t.body_text ?? prev.body_text,
    }));
    notify.info(`テンプレート「${t.name}」を読み込みました`);
  };

  const handleSaveTemplate = async () => {
    if (!tplName.trim()) return;
    const r = await mailApi.saveTemplate(
      { name: tplName.trim(), subject: form.subject || null, from_address_id: form.from_address_id, blocks_json: form.blocks_json, body_text: form.body_text },
      operator,
    );
    if (!r.ok) return notify.error(r.error);
    notify.success('テンプレートとして保存しました');
    setTplDialog(false);
    setTplName('');
    masters.reload();
  };

  // 再読込 (読者の変更後など) では画面を消さない。初回の読み込み中だけ全面ローディングにする
  if (!initialized || (masters.loading && masters.lists.length === 0)) return <LoadingView message="配信作成を準備しています..." height="80vh" />;
  if (editingId != null && !existing.campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{existing.error || '配信が見つかりません'}</Alert>
        <Button sx={{ ...STANDARD_TEXT_BUTTON_SX, mt: 2 }} startIcon={<Icons.ArrowBack />} onClick={() => navigate('/mail-delivery')}>
          配信履歴へ戻る
        </Button>
      </Box>
    );
  }

  // ---------------------------------------------------------------- 各ステップの描画
  const renderRecipients = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], mb: 1 }}>配信リスト (複数選択は OR)</Typography>
        {masters.lists
          .filter((l) => l.is_active || form.list_codes.includes(l.list_code))
          .map((l) => {
            const checked = form.list_codes.includes(l.list_code);
            const toggle = () => set('list_codes', checked ? form.list_codes.filter((c) => c !== l.list_code) : [...form.list_codes, l.list_code]);
            return (
              // 1 行 = チェック+リスト名 | 件数 | 説明 (はみ出しは省略) | 中身を見る。各列は折り返さない
              <Box
                key={l.list_code}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto max-content minmax(0, 1fr) max-content',
                  alignItems: 'center',
                  columnGap: 1.5,
                  py: 0.25,
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <FormControlLabel
                  sx={{ ml: 0, mr: 0, whiteSpace: 'nowrap' }}
                  control={<Checkbox size="small" checked={checked} onChange={toggle} />}
                  label={<Typography sx={{ fontSize: 13.5, whiteSpace: 'nowrap' }}>{l.list_name}</Typography>}
                />
                <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  配信中 {Number(l.active_count || 0).toLocaleString()} 件 / 登録 {Number(l.total_count || 0).toLocaleString()} 件
                </Typography>
                <Tooltip title={l.description || ''} disableHoverListener={!l.description}>
                  <Typography noWrap sx={{ fontSize: 11.5, color: 'text.disabled', minWidth: 0 }}>
                    {l.description || ''}
                  </Typography>
                </Tooltip>
                <Button
                  size="small"
                  startIcon={<Icons.People sx={{ fontSize: 15 }} />}
                  onClick={() => setMembersList(l)}
                  sx={{ fontSize: 12, textTransform: 'none', whiteSpace: 'nowrap', py: 0.25 }}
                >
                  中身を見る
                </Button>
              </Box>
            );
          })}
        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], mb: 1 }}>絞り込み (任意)</Typography>
        <TextField
          size="small"
          label="都道府県 (法人住所の先頭一致)"
          value={form.filter_json?.prefecture || ''}
          onChange={(e) => set('filter_json', e.target.value ? { ...(form.filter_json || {}), prefecture: e.target.value } : null)}
          placeholder="例 福岡県"
          sx={{ ...fieldSx, width: 260 }}
        />
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>
          配信停止・バウンス停止・解除済みの読者と、送信禁止リストのアドレスは常に除外されます。同じメールアドレスは 1 通にまとめます。
        </Typography>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', bgcolor: '#f8fafc' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700] }}>配信対象</Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 700, color: blueGrey[800], lineHeight: 1.2, my: 0.5 }}>
          {countLoading ? '…' : (recipients?.count ?? 0).toLocaleString()} <span style={{ fontSize: 14 }}>件</span>
        </Typography>
        {recipients && recipients.suppressed > 0 && (
          <Typography sx={{ fontSize: 12, color: '#c62828' }}>送信禁止リストにより {recipients.suppressed.toLocaleString()} 件を除外</Typography>
        )}
        {recipients && recipients.sample.length > 0 && (
          <>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5, mb: 0.5 }}>見本 (先頭 {recipients.sample.length} 件)</Typography>
            {recipients.sample.map((s) => (
              <Typography key={s.email} sx={{ fontSize: 12, lineHeight: 1.5 }} noWrap title={`${s.company_name || ''} ${s.recipient_name || ''} ${s.email}`}>
                {s.company_name || '(会社名なし)'} {s.recipient_name || ''} 〈{s.email}〉
              </Typography>
            ))}
          </>
        )}
      </Paper>
    </Box>
  );

  const renderContent = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 640px)', gap: 2, alignItems: 'start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <TextField select size="small" required label="送信元" value={form.from_address_id ?? ''} onChange={(e) => set('from_address_id', Number(e.target.value))} sx={fieldSx}>
            {masters.fromAddresses.map((f) => (
              <MenuItem key={f.id} value={f.id} sx={{ fontSize: 13 }}>
                {f.display_name ? `${f.display_name} 〈${f.email}〉` : f.email}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="返信先 (空なら送信元の既定)"
            value={form.reply_to ?? ''}
            onChange={(e) => set('reply_to', e.target.value || null)}
            placeholder={fromAddress?.reply_to || fromAddress?.email || ''}
            sx={fieldSx}
          />
          <TextField size="small" required label="件名" value={form.subject} onChange={(e) => set('subject', e.target.value)} sx={{ ...fieldSx, gridColumn: '1 / -1' }} />
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="テンプレートを読み込む"
              value=""
              onChange={(e) => {
                const t = masters.templates.find((x) => x.id === Number(e.target.value));
                if (t) applyTemplate(t);
              }}
              sx={{ ...fieldSx, width: 280 }}
              helperText={masters.templates.length === 0 ? 'テンプレートはまだありません (本文を作って右の「テンプレートとして保存」)' : undefined}
            >
              {masters.templates.map((t) => (
                <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            <Button size="small" variant="outlined" startIcon={<Icons.Save />} disabled={form.blocks_json.length === 0} onClick={() => setTplDialog(true)} sx={{ fontSize: 12, textTransform: 'none' }}>
              テンプレートとして保存
            </Button>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
              差し込みコード {'{{company_name}}'} {'{{recipient_branch}}'} {'{{recipient_name}}'} {'{{email}}'} {'{{unsubscribe_url}}'}
            </Typography>
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], mb: 1 }}>本文 (ブロック)</Typography>
          <MailBlocksEditor blocks={form.blocks_json} onChange={(b) => set('blocks_json', b)} campaignId={form.id ?? null} />
          <Divider sx={{ my: 1.5 }} />
          <TextField
            size="small"
            multiline
            minRows={2}
            label="テキストパート (空なら本文から自動生成)"
            value={form.body_text ?? ''}
            onChange={(e) => set('body_text', e.target.value || null)}
            sx={{ ...fieldSx, width: '100%' }}
          />
        </Paper>
      </Box>
      <Box sx={{ position: 'sticky', top: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], flex: 1 }}>プレビュー (差し込み見本・解除フッター付き)</Typography>
          <Tooltip title="プレビューを更新">
            <IconButton size="small" onClick={refreshPreview}>
              <Icons.Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <MailPreviewFrame html={previewHtml} height={560} emptyMessage="ブロックを追加するとプレビューが表示されます" />
      </Box>
    </Box>
  );

  const renderTest = () => (
    <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', maxWidth: 760 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], mb: 1 }}>テスト配信</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
        実際に SES から送信して表示崩れを確認します。件名の先頭に【テスト】が付き、宛名は見本 (テスト 太郎 様) で差し込まれます。配信結果には記録されません。
        本番アクセス承認前 (サンドボックス中) は SES で検証済みのアドレスにしか届きません。
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField size="small" label="送信先 (複数はカンマ区切り)" value={testTo} onChange={(e) => setTestTo(e.target.value)} sx={{ ...fieldSx, flex: 1 }} />
        <Button sx={STANDARD_CTA_BUTTON_SX} startIcon={<Icons.Send />} disabled={testSending} onClick={handleSendTest}>
          {testSending ? '送信中...' : 'テスト送信'}
        </Button>
      </Box>
      {testResult && (
        <Alert severity="success" sx={{ mt: 1.5, py: 0 }}>
          {testResult}
        </Alert>
      )}
      {previewText && (
        <>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: blueGrey[700], mt: 2, mb: 0.5 }}>テキストパート (HTML を表示できない受信環境向け)</Typography>
          <Box component="pre" sx={{ fontSize: 12, bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, whiteSpace: 'pre-wrap', m: 0, maxHeight: 240, overflow: 'auto' }}>
            {previewText}
          </Box>
        </>
      )}
    </Paper>
  );

  const renderSchedule = () => (
    <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', maxWidth: 760 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], mb: 1 }}>配信日時</Typography>
      <RadioGroup value={sendMode} onChange={(e) => setSendMode(e.target.value as 'now' | 'schedule')}>
        <FormControlLabel value="now" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5 }}>今すぐ配信 (確認ステップで「配信する」を押した直後に開始)</Typography>} />
        <FormControlLabel value="schedule" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5 }}>日時を指定して予約</Typography>} />
      </RadioGroup>
      {sendMode === 'schedule' && (
        <Box sx={{ ml: 4, mt: 0.5 }}>
          <TextField
            size="small"
            type="datetime-local"
            label="配信日時 (日本時間)"
            value={scheduleLocal}
            onChange={(e) => setScheduleLocal(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={scheduleInPast}
            helperText={scheduleInPast ? '現在より後の日時にしてください' : '例 日曜 16:00。予約後も配信開始前なら取り消せます'}
            sx={{ ...fieldSx, width: 260 }}
          />
        </Box>
      )}
    </Paper>
  );

  const renderConfirm = () => {
    const rows: [string, React.ReactNode][] = [
      ['配信先', `${selectedLists.map((l) => l.list_name).join('、') || '(未選択)'}  ${(recipients?.count ?? 0).toLocaleString()} 件`],
      ['送信元', fromAddress ? `${fromAddress.display_name || ''} 〈${fromAddress.email}〉` : '(未選択)'],
      ['返信先', form.reply_to || fromAddress?.reply_to || fromAddress?.email || ''],
      ['件名', form.subject],
      ['本文', `${form.blocks_json.length} ブロック`],
      ['配信日時', sendMode === 'schedule' ? `${fmtDateTime(scheduledIso)} に予約` : '今すぐ配信'],
    ];
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 640px)', gap: 2, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: blueGrey[700], mb: 1 }}>配信内容の確認</Typography>
          <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', '& td': { fontSize: 13, py: 0.75, px: 1, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' } }}>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <td style={{ width: 110, color: '#607d8b', fontWeight: 700 }}>{k}</td>
                  <td>{v}</td>
                </tr>
              ))}
            </tbody>
          </Box>
          {stepError && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
              {stepError}
            </Alert>
          )}
          {recipients && recipients.count === 0 && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
              配信対象が 0 件です。配信先を見直してください。
            </Alert>
          )}
        </Paper>
        <MailPreviewFrame html={previewHtml} height={560} />
      </Box>
    );
  };

  const confirmDisabled = !!stepError || starting || saving || !recipients || recipients.count === 0 || !form.from_address_id || !form.subject.trim() || form.blocks_json.length === 0;

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 2, width: '96%', margin: '0 auto' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/mail-delivery')}>
          <Icons.ArrowBack />
        </IconButton>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: blueGrey[700] }}>{form.id ? `配信の編集 No ${form.id}` : '新規配信作成'}</Typography>
        {existing.campaign && <CampaignStatusChip status={existing.campaign.status} />}
        {form.copied_from_id && <Chip size="small" variant="outlined" label={`No ${form.copied_from_id} から複製`} sx={{ fontSize: 11.5, height: 22 }} />}
        <Box sx={{ flex: 1 }} />
        <Button size="small" sx={STANDARD_TEXT_BUTTON_SX} startIcon={<Icons.Save />} disabled={saving} onClick={() => saveDraft()}>
          {saving ? '保存中...' : '下書き保存'}
        </Button>
      </Box>
      <Stepper activeStep={step} sx={{ mb: 2, '& .MuiStepLabel-label': { fontSize: 13 } }}>
        {STEPS.map((label, i) => (
          <Step key={label} completed={i < step}>
            <StepLabel onClick={() => i < step && setStep(i)} sx={{ cursor: i < step ? 'pointer' : 'default' }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* 本体 (ステップごと) */}
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
        {step === 0 && renderRecipients()}
        {step === 1 && renderContent()}
        {step === 2 && renderTest()}
        {step === 3 && renderSchedule()}
        {step === 4 && renderConfirm()}
      </Box>

      {/* フッター */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.5, mt: 1, borderTop: '1px solid #e2e8f0' }}>
        {stepError && step < 4 && <Typography sx={{ fontSize: 12.5, color: '#c62828' }}>{stepError}</Typography>}
        <Box sx={{ flex: 1 }} />
        <Button sx={STANDARD_TEXT_BUTTON_SX} onClick={() => navigate('/mail-delivery')}>
          一覧へ戻る
        </Button>
        <Button sx={STANDARD_TEXT_BUTTON_SX} disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
          前へ
        </Button>
        {step < STEPS.length - 1 ? (
          <Button sx={STANDARD_CTA_BUTTON_SX} disabled={!!stepError} onClick={() => setStep((s) => s + 1)}>
            次へ
          </Button>
        ) : (
          <Button sx={STANDARD_CTA_BUTTON_SX} startIcon={sendMode === 'schedule' ? <Icons.Schedule /> : <Icons.Send />} disabled={confirmDisabled} onClick={handleStart}>
            {starting ? '処理中...' : sendMode === 'schedule' ? '予約する' : '配信する'}
          </Button>
        )}
      </Box>

      {/* 配信リストの中身 */}
      <MailListMembersDialog
        list={membersList}
        operator={operator}
        onClose={() => setMembersList(null)}
        onChanged={() => { setCountKey((k) => k + 1); masters.reload(); }}
      />

      {/* テンプレート保存ダイアログ */}
      <Dialog open={tplDialog} onClose={() => setTplDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, color: blueGrey[600], borderBottom: '1px solid #f1f5f9' }}>
          <Icons.Save sx={{ fontSize: 18 }} />
          テンプレートとして保存
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField size="small" autoFocus fullWidth label="テンプレート名" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="例 日曜配信 (建売・土地情報)" sx={fieldSx} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>現在の件名・送信元・本文ブロックを雛形として保存します。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTplDialog(false)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleSaveTemplate} disabled={!tplName.trim()} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>
            保存する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MailDeliveryCampaignEditor;
